import { useState } from "react";

import { cn } from "@pontimed/ui";
import { ChevronDown } from "lucide-react";

import { TableCell, TableRow } from "./table";

interface MobileDetailField {
  label: React.ReactNode;
  value: React.ReactNode;
}

/**
 * A table row that expands on mobile to show hidden column data.
 * On desktop (md+), renders a normal row. On mobile, adds a tap-to-expand
 * accordion below the row with all hidden fields as a key-value list.
 *
 * Cloned 1:1 from src/components/ui/responsive-table.tsx (legacy NextJS) per
 * D21 NO REINVENTION. Imports adjusted to apps/admin paths.
 */
export function ExpandableRow({
  children,
  mobileDetails,
  className,
  colSpan = 20,
}: {
  children: React.ReactNode;
  mobileDetails: MobileDetailField[];
  className?: string;
  colSpan?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        className={cn("cursor-pointer md:cursor-default", className)}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {children}
        <TableCell className="w-8 p-1 md:hidden">
          <ChevronDown
            className={cn(
              "text-muted-foreground size-4 transition-transform",
              expanded && "rotate-180",
            )}
          />
        </TableCell>
      </TableRow>
      {expanded && (
        <tr className="bg-muted/30 border-b md:hidden">
          <td colSpan={colSpan} className="px-4 py-3">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {mobileDetails.map((field, i) => (
                <div key={i}>
                  <dt className="text-muted-foreground text-xs">{field.label}</dt>
                  <dd className="font-medium">{field.value}</dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}
